import { useNavigate } from '@tanstack/react-router';
import { memo } from 'react';

import { merge } from '@/components';
import { Button, ButtonProps, Text } from '@/components/core';
import { AIModelIcon } from '@/components/icons';
import { useRemainingDaysOfTrial, useRudderAnalytics } from '@/hooks';
import { toOrganizationId, toPlanId, useGetOrganization, useGetPlan } from '@/hooks/api';
import { OmitChildren } from '@/utils';

export type UpgradeCardProps = OmitChildren<ButtonProps> & {
  organizationId: string;
};

const IUpgradeCard = ({ organizationId, className, ...rest }: UpgradeCardProps) => {
  const { data: organization } = useGetOrganization(toOrganizationId(organizationId));
  const { data: plan } = useGetPlan(toPlanId(organization?.planId));
  const navigate = useNavigate();
  const { track } = useRudderAnalytics();

  const { daysRemaining } = useRemainingDaysOfTrial();

  if (!organization || !plan || daysRemaining == null) return null;

  const trialNearlyEnded = (daysRemaining ?? 0) < 4;

  return (
    <Button
      className={merge(
        'border border-dashed',
        trialNearlyEnded && 'bg-warning-25 border-warning-300',
        !trialNearlyEnded && 'bg-gray-25 border-gray-300',
        className
      )}
      onPress={track('Upgrade Clicked', () => navigate({ to: '/settings/plans' }))}
      variant="plain"
      {...rest}
    >
      <AIModelIcon
        className={merge('!size-3', trialNearlyEnded && '!text-warning-500', !trialNearlyEnded && '!text-primary-500')}
      />
      <Text size="xs" className="!text-gray-700">
        <span className="!font-semibold">{daysRemaining} days</span> left on free trial
      </Text>
    </Button>
  );
};

export const UpgradeCard = memo(IUpgradeCard) as typeof IUpgradeCard;
